import React, { useState, useEffect, useRef } from 'react';
import EmojiPicker from '../components/ui/EmojiPicker';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';

const SIGNAL_URL = 'https://turn.lepb.fr:3004';
const TURN_CONFIG = {
  iceServers: [
    { urls: 'stun:turn.lepb.fr:3478' },
    { urls: 'turn:turn.lepb.fr:3478', username: 'mjfans', credential: 'lepbsecret2026' }
  ]
};

export default function LiveRoom() {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isHost = searchParams.get('host') === '1';
  const titre = searchParams.get('titre') || 'Live';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [viewers, setViewers] = useState(0);
  const [status, setStatus] = useState('Connexion...');
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [needsPlay, setNeedsPlay] = useState(false);
  const [viewersList, setViewersList] = useState([]);
  const snapshotIntervalRef = useRef(null);

  const socketRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const pcsRef = useRef({});
  const chatEndRef = useRef(null);
  const chatBoxRef = useRef(null);
  const initializedRef = useRef(false);

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const pseudo = user?.username || user?.email?.split('@')[0] || 'Anonyme';

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const socket = io(SIGNAL_URL, { transports: ['websocket'], reconnection: false });
    socketRef.current = socket;

    socket.on('connect', async () => {
      if (isHost) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          localStreamRef.current = stream;
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
            const startSnapshot = () => {
              snapshotIntervalRef.current = setInterval(() => {
                const video = localVideoRef.current;
                if (!video) return;
                try {
                  const canvas = document.createElement('canvas');
                  canvas.width = 320; canvas.height = 180;
                  canvas.getContext('2d').drawImage(video, 0, 0, 320, 180);
                  const image = canvas.toDataURL('image/jpeg', 0.5);
                  socketRef.current?.emit('snapshot', { roomId, image });
                } catch(e) {}
              }, 10000);
            };
            localVideoRef.current.onloadeddata = startSnapshot;
            setTimeout(startSnapshot, 3000);
          }
          socket.emit('create-room', { roomId, titre, pseudo });
          setStatus('Live en cours');
        } catch (e) {
          setStatus('Erreur camera : ' + e.message);
        }
      } else {
        socket.emit('join-room', { roomId, pseudo });
        setStatus('En attente du broadcaster...');
      }
    });

    socket.on('rooms-updated', rooms => {
      const room = rooms.find(r => r.id === roomId);
      if (room) setViewers(room.viewers);
    });

    socket.on('viewer-joined', async ({ viewerId, pseudo: vPseudo }) => {
      try {
        setMessages(m => [...m, { system: true, text: vPseudo + ' a rejoint le live' }]);
        if (pcsRef.current[viewerId]) {
          pcsRef.current[viewerId].close();
          delete pcsRef.current[viewerId];
        }
        const pc = new RTCPeerConnection(TURN_CONFIG);
        pcsRef.current[viewerId] = pc;
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(t => pc.addTrack(t, localStreamRef.current));
        }
        pc.onicecandidate = e => {
          if (e.candidate) socket.emit('ice-to-viewer', { viewerId, candidate: e.candidate });
        };
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('offer-to-viewer', { viewerId, offer });
      } catch (e) {
        console.error('viewer-joined error:', e);
      }
    });

    socket.on('offer-from-viewer', async ({ answer, viewerId }) => {
      try {
        const pc = pcsRef.current[viewerId];
        if (!pc || pc.signalingState !== 'have-local-offer') return;
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
      } catch (e) {
        console.error('offer-from-viewer error:', e);
      }
    });

    socket.on('offer-from-host', async ({ offer }) => {
      try {
        if (pcsRef.current['host']) {
          pcsRef.current['host'].close();
          delete pcsRef.current['host'];
        }
        const pc = new RTCPeerConnection(TURN_CONFIG);
        pcsRef.current['host'] = pc;
        pc.ontrack = e => {
          remoteStreamRef.current = e.streams[0];
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = e.streams[0];
            remoteVideoRef.current.play().catch(() => setNeedsPlay(true));
          }
        };
        pc.onicecandidate = e => {
          if (e.candidate) socket.emit('ice-to-host', { roomId, candidate: e.candidate, viewerId: socket.id });
        };
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('offer-to-host', { roomId, answer, viewerId: socket.id });
        setStatus('Connecte au live');
      } catch (e) {
        console.error('offer-from-host error:', e);
      }
    });

    socket.on('ice-from-host', ({ candidate }) => {
      pcsRef.current['host']?.addIceCandidate(new RTCIceCandidate(candidate));
    });

    socket.on('ice-from-viewer', ({ candidate, viewerId }) => {
      pcsRef.current[viewerId]?.addIceCandidate(new RTCIceCandidate(candidate));
    });

    socket.on('viewer-left', ({ viewerId }) => {
      pcsRef.current[viewerId]?.close();
      delete pcsRef.current[viewerId];
    });

    socket.on('host-left', () => {
      setStatus('Le live est termine');
      setTimeout(() => navigate('/live'), 3000);
    });

    socket.on('chat-message', msg => {
      setMessages(m => [...m, msg]);
    });

    socket.on('room-not-found', () => {
      setStatus('Live introuvable');
      setTimeout(() => navigate('/live'), 2000);
    });

    if (isHost) {
      snapshotIntervalRef.current = setInterval(() => {
        const video = localVideoRef.current;
        if (!video || video.readyState < 2) return;
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 320; canvas.height = 180;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, 320, 180);
          const image = canvas.toDataURL('image/jpeg', 0.5);
          socketRef.current?.emit('snapshot', { roomId, image });
        } catch(e) {}
      }, 10000);
    }

    return () => {
      if (snapshotIntervalRef.current) clearInterval(snapshotIntervalRef.current);
      Object.values(pcsRef.current).forEach(pc => pc.close());
      localStreamRef.current?.getTracks().forEach(t => t.stop());
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    const box = chatBoxRef.current; if (box) box.scrollTop = box.scrollHeight;
  }, [messages]);

  const envoyerMessage = () => {
    if (!input.trim()) return;
    socketRef.current?.emit('chat-message', { roomId, pseudo, message: input });
    setInput('');
  };

  const toggleMute = () => {
    localStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setMuted(m => !m);
  };

  const toggleVideo = () => {
    localStreamRef.current?.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
    setVideoOff(v => !v);
  };

  const terminerLive = () => {
    socketRef.current?.disconnect();
    navigate('/live');
  };

  return (
    <div className="live-outer" style={{ background: '#0a0608', height: 'calc(100dvh - 56px)', minHeight: 0, fontFamily: 'Arial,sans-serif', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ background: '#1a0808', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/live')} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>← Retour</button>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, color: '#fff' }}>{titre}</div>
          <div style={{ background: '#ff2222', color: '#fff', fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
            {isHost ? 'LIVE' : viewers + ' spectateurs'}
          </div>
        </div>
        <div style={{ fontSize: 12, color: (status === 'Live en cours' || status === 'Connecte au live') ? '#2ecc71' : '#e8a0a0' }}>{status}</div>
      </div>

      <div className="live-grid" style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, gap: 16 }}>
          <div style={{ width: '100%', maxWidth: 720, aspectRatio: '16/9', background: '#1a0808', borderRadius: 12, overflow: 'hidden', position: 'relative' }}>
            {isHost ? (
              <video ref={localVideoRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <video ref={remoteVideoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
            {(isHost && videoOff) && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14 }}>Camera desactivee</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {isHost ? (
              <>
                <button onClick={toggleMute} style={{ padding: '10px 20px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.2)', background: muted ? '#8b1a2a' : 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, cursor: 'pointer' }}>
                  {muted ? 'Micro off' : 'Micro on'}
                </button>
                <button onClick={toggleVideo} style={{ padding: '10px 20px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.2)', background: videoOff ? '#8b1a2a' : 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, cursor: 'pointer' }}>
                  {videoOff ? 'Camera off' : 'Camera on'}
                </button>
                <button onClick={terminerLive} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#8b1a2a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Terminer le live
                </button>
              </>
            ) : (
              <button onClick={() => navigate('/live')} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#8b1a2a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Quitter le live
              </button>
            )}
          </div>
        </div>

        <div style={{ background: '#1a0808', borderLeft: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: '0.1em' }}>
            CHAT EN DIRECT
          </div>
          <div ref={chatBoxRef} className="live-chatbox" style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, maxHeight: 'calc(100vh - 200px)' }}>
            {messages.map((m, i) => (
              <div key={i}>
                {m.system ? (
                  <div style={{ textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>{m.text}</div>
                ) : (
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#e8a0a0' }}>{m.pseudo} </span>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginRight: 4 }}>{m.time}</span>
                    <div style={{ fontSize: 13, color: '#fff', lineHeight: 1.5, marginTop: 2 }}>{m.message}</div>
                  </div>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 8 }}>
            <EmojiPicker onEmojiClick={emoji => setInput(prev => prev + emoji)} />
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && envoyerMessage()}
              placeholder="Ecrire un message..."
              style={{ flex: 1, padding: '8px 12px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12, outline: 'none' }} />
            <button onClick={envoyerMessage}
              style={{ padding: '8px 14px', borderRadius: 20, border: 'none', background: '#8b1a2a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>→</button>
          </div>
        </div>
      </div>
    </div>
  );
}
